import { useQuery } from '@tanstack/react-query'
import { dashboardConfigApi } from '@/lib/api'
import { BaseWidget, StatWidget } from './BaseWidget'
import { Bell } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import type { NotificationData } from '@/types'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'

// Notifications Widget
export function NotificationsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'NOTIFICATIONS'],
    queryFn: () => dashboardConfigApi.getWidgetData<NotificationData[]>('NOTIFICATIONS'),
    refetchInterval: 60000, // Refresh every minute
  })

  const notifications = data?.data || []

  return (
    <BaseWidget
      title={t('dashboard.notifications')}
      icon={<Bell className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="space-y-2">
        {notifications.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noNotifications')}</p>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.notification_id}
              className={`p-2 rounded-lg ${notification.is_read ? 'bg-muted/30' : 'bg-muted/50'}`}
            >
              <div className="flex items-start gap-2">
                <Bell className={`h-4 w-4 mt-0.5 ${notification.is_read ? 'text-muted-foreground' : 'text-primary'}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${notification.is_read ? '' : 'font-medium'}`}>{notification.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{notification.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">{formatDate(notification.created_at)}</p>
                </div>
                {!notification.is_read && (
                  <Badge variant="default" className="text-xs">New</Badge>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

// Unread Notifications Count Widget
export function UnreadNotificationsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'UNREAD_NOTIFICATIONS'],
    queryFn: () => dashboardConfigApi.getWidgetData<{ unread_count: number }>('UNREAD_NOTIFICATIONS'),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const count = data?.data?.unread_count || 0

  return (
    <StatWidget
      title={t('dashboard.unreadNotifications')}
      value={count}
      subtitle={count === 0 ? t('dashboard.allCaughtUp') : t('dashboard.pendingNotifications')}
      icon={<Bell className={`h-4 w-4 ${count > 0 ? 'text-primary' : 'text-muted-foreground'}`} />}
      isLoading={isLoading}
    />
  )
}

